<script setup lang="ts">
import pathToRegexp from 'path-to-regexp'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { RouteRecordNormalized } from 'vue-router'
import { Breadcrumb, type BreadcrumbItem } from '@/shared/ui/Breadcrumb'

const route = useRoute()
const router = useRouter()

const crumbs = computed<BreadcrumbItem[]>(() => {
  const matched: RouteRecordNormalized[] = route.matched
  const leaf = matched[matched.length - 1]
  if (!leaf || leaf.meta.root) return []

  const root = router.getRoutes().find((record) => record.meta.root)

  // A modal is a sibling route of the list it opens over, not its child, so `matched` can
  // never name the page underneath — every page whose path prefixes this one is above it.
  const pages = [
    ...matched.slice(0, -1).filter((record) => record.meta.title),
    ...router
      .getRoutes()
      .filter(
        (record) =>
          record.meta.title &&
          !record.meta.hidden &&
          leaf.path.startsWith(`${record.path}/`)
      )
      .sort((a, b) => a.path.length - b.path.length),
    ...(leaf.meta.title ? [leaf] : [])
  ]

  // Every crumb is "a title, or nothing". `meta.title` is optional in
  // `app/routeMeta.d.ts`, and the `.find` above does not narrow it, so each source tests its
  // own title instead of asserting one — a record that lost its title drops out of the
  // trail rather than printing a blank crumb.
  return [
    ...(root?.meta.title && root.path !== leaf.path
      ? [{ title: root.meta.title, to: root.path }]
      : []),
    ...pages.flatMap((record) =>
      record.meta.title
        ? [
            {
              title: record.meta.title,
              to: pathToRegexp.compile(record.path)(route.params)
            }
          ]
        : []
    )
  ].filter(
    (crumb, index, all) =>
      all.findIndex((other) => other.title === crumb.title) === index
  )
})
</script>

<template>
  <Breadcrumb :items="crumbs" class="app-breadcrumb" />
</template>

<style lang="scss" scoped>
.app-breadcrumb {
  display: inline-block;
  line-height: 50px;
  margin-left: 8px;
}
</style>
